// src/components/floorplans/MapFloorplanOverlay.js
import { useEffect, useRef } from "react";

/**
 * Creates and returns the custom Google Maps OverlayView class for rotated floorplans.
 */
function createGroundOverlayClass() {
  if (!window.google || !window.google.maps || !window.google.maps.OverlayView) {
    return null;
  }

  return class RotatedGroundOverlay extends window.google.maps.OverlayView {
    constructor(bounds, image, rotation, opacity, map, zIndex = 4) {
      super();
      this.bounds = bounds;
      this.image = image;
      this.rotation = rotation || 0;
      this.opacity = opacity !== undefined ? opacity : 0.85;
      this.zIndex = zIndex;
      this.div = null;
      this.setMap(map);
    }

    onAdd() {
      this.div = document.createElement("div");
      this.div.style.borderStyle = "none";
      this.div.style.borderWidth = "0px";
      this.div.style.position = "absolute";
      this.div.style.pointerEvents = "none";
      this.div.style.zIndex = String(this.zIndex);

      const img = document.createElement("img");
      img.src = this.image;
      img.style.width = "100%";
      img.style.height = "100%";
      img.style.position = "absolute";
      img.style.opacity = String(this.opacity);
      img.style.objectFit = "fill";
      this.div.appendChild(img);

      const panes = this.getPanes();
      if (panes && panes.overlayLayer) {
        panes.overlayLayer.appendChild(this.div);
      }
    }

    draw() {
      const overlayProjection = this.getProjection();
      if (!overlayProjection || !this.div) return;

      const sw = overlayProjection.fromLatLngToDivPixel(
        new window.google.maps.LatLng(this.bounds.south, this.bounds.west)
      );
      const ne = overlayProjection.fromLatLngToDivPixel(
        new window.google.maps.LatLng(this.bounds.north, this.bounds.east)
      );

      if (sw && ne) {
        const width = ne.x - sw.x;
        const height = sw.y - ne.y;
        this.div.style.left = `${sw.x}px`;
        this.div.style.top = `${ne.y}px`;
        this.div.style.width = `${Math.max(width, 2)}px`;
        this.div.style.height = `${Math.max(height, 2)}px`;
        this.div.style.transform = `rotate(${this.rotation}deg)`;
        this.div.style.transformOrigin = "center center";

        const img = this.div.querySelector("img");
        if (img) {
          img.src = this.image;
          img.style.opacity = String(this.opacity);
        }
      }
    }

    update(bounds, image, rotation, opacity) {
      this.bounds = bounds;
      this.image = image;
      this.rotation = rotation;
      this.opacity = opacity;
      this.draw();
    }

    onRemove() {
      if (this.div && this.div.parentNode) {
        this.div.parentNode.removeChild(this.div);
        this.div = null;
      }
    }
  };
}

export default function MapFloorplanOverlay({
  map,
  floorplan,
  visible = true,
  opacity = 0.85,
  zIndex = 4,
}) {
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!map || !window.google || !floorplan?.image_url || !visible) {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
      }
      return;
    }

    const bounds = {
      north: parseFloat(floorplan.north),
      south: parseFloat(floorplan.south),
      east: parseFloat(floorplan.east),
      west: parseFloat(floorplan.west),
    };

    if (
      isNaN(bounds.north) ||
      isNaN(bounds.south) ||
      isNaN(bounds.east) ||
      isNaN(bounds.west)
    ) {
      return;
    }

    const rotation = parseFloat(floorplan.rotation) || 0;
    const effectiveOpacity =
      opacity !== undefined
        ? opacity
        : floorplan.opacity !== undefined
        ? parseFloat(floorplan.opacity)
        : 0.85;

    const OverlayClass = createGroundOverlayClass();
    if (!OverlayClass) return;

    if (!overlayRef.current) {
      overlayRef.current = new OverlayClass(
        bounds,
        floorplan.image_url,
        rotation,
        effectiveOpacity,
        map,
        zIndex
      );
    } else {
      overlayRef.current.update(
        bounds,
        floorplan.image_url,
        rotation,
        effectiveOpacity
      );
    }

    return () => {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
      }
    };
  }, [map, floorplan, visible, opacity, zIndex]);

  return null;
}
